import { Component, Inject, Injector, ViewChild } from '@angular/core';
import { TabDetailPanelParameters } from '@common-modules/dependencies/navigation/tab-detail-panel-parameters';
import { WidgetSettingsToken } from '@common-modules/dynamic-layout/dynamic-layout.tokens';
import { DynamicLayoutComponentApi } from '@common-modules/dynamic-layout/dynamic-layout/dynamic-layout-component-api';
import { DynamicLayoutComponent } from '@common-modules/dynamic-layout/dynamic-layout/dynamic-layout.component';
import { DynamicLayoutBuilderService } from '@common-modules/dynamic-layout/layouts/dynamic-layout-builder-service';
import { SimpleDynamicLayoutSettings } from '@common-modules/dynamic-layout/layouts/simple-dynamic-layout-settings';
import { DynamicLayoutItemSettings } from '@common-modules/dynamic-layout/models/dynamic-layout-item-settings';
import { DynamicLayoutLevels } from '@common-modules/dynamic-layout/models/dynamic-layout-levels';
import { DynamicLayoutSettings } from '@common-modules/dynamic-layout/models/dynamic-layout-settings';
import { DynamicLayoutSettingsLoadOptions } from '@common-modules/dynamic-layout/models/dynamic-layout-settings-load-options';
import { LayoutLoadOptions } from '@common-modules/dynamic-layout/models/layout-load-options';
import { StateWidgetSettings } from '@common-modules/redux/models/state-widget-settings';
import { AppModules } from '@common-modules/shared/app-modules.enum';
import { PermissionsRequest } from '@common-modules/shared/auth/models/permissions-request';
import { BaseWidgetComponent } from '@common-modules/shared/component/base-widget.component';
import { DialogService } from '@common-modules/shared/dialogs/dialogs.service';
import { SpinnerService } from '@common-modules/wlm-spinner/spinner.service';
import { finalize, map, Observable } from 'rxjs';
import { DashboardDefinition } from '../dashboard-definition';
import { DashboardIdentity } from '../dashboard-identity';
import { DashboardsService } from '../dashboards.service';

const COMPONENT_SELECTOR = 'wlm-dashboard-widget';

@Component({
  selector: COMPONENT_SELECTOR,
  templateUrl: './dashboard-widget.component.html',
  styleUrl: './dashboard-widget.component.scss',
  standalone: false,
})
export class DashboardWidgetComponent extends BaseWidgetComponent {
  private _dynamicLayoutComponent: DynamicLayoutComponentApi;
  @ViewChild(DynamicLayoutComponent) set setDynamicLayoutComponent(
    value: DynamicLayoutComponentApi,
  ) {
    if (value) {
      this._dynamicLayoutComponent = value;
    }
  }

  readonly allowEmptyRoot = true;
  readonly specificLevel = DynamicLayoutLevels.Inner;
  readonly T_SCOPE = `${AppModules.RightPanel}.${COMPONENT_SELECTOR}`;

  dynamicLayoutSettingsLoadOptions: DynamicLayoutSettingsLoadOptions;
  private _dynamicLayoutSettings: DynamicLayoutSettings;
  get dynamicLayoutSettings(): DynamicLayoutSettings {
    return this._dynamicLayoutSettings;
  }
  set dynamicLayoutSettings(value: DynamicLayoutSettings) {
    this._dynamicLayoutSettings = value;
    this.dynamicLayoutSettingsLoadOptions = {
      settings: this.dynamicLayoutSettings,
      loadOptions: this._layoutLoadOptions,
    };
  }
  private _dashboard: DashboardIdentity;
  private _currentLayoutSettings: DynamicLayoutSettings;

  private readonly _persistencyArea = 'Dahsboard';
  private readonly _statePage = this._persistencyArea;
  private readonly _layoutLoadOptions: LayoutLoadOptions = {
    disablePersistencyLoad: true,
    disablePersistencySave: true,
  };
  dashboardGroupPermissions: PermissionsRequest;

  setLoading: (loading: boolean) => void;

  constructor(
    injector: Injector,
    @Inject(WidgetSettingsToken) widgetSettings: StateWidgetSettings,
    private readonly _dashboardsService: DashboardsService,
    private readonly _dynamicLayoutBuilderService: DynamicLayoutBuilderService,
    private readonly _dialogService: DialogService,
    private readonly _spinnerService: SpinnerService,
  ) {
    super(injector, widgetSettings);
    this._dashboard = widgetSettings.params.dashboard;
    this.setLoading = this._spinnerService.buildSetLoadingFn();
  }

  mapInitParameters(parameters: TabDetailPanelParameters) {}
  init(): void {}

  onWidgetInit(): void {
    this.buildDashboard();
  }

  private buildDashboard(): void {
    this._dashboardsService
      .loadDashboard(this._dashboard)
      .subscribe((dashboardDefinition: DashboardDefinition) => {
        if (dashboardDefinition?.dynamicLayoutSettings) {
          dashboardDefinition.dynamicLayoutSettings.disableReorder = false;
          this.dynamicLayoutSettings = dashboardDefinition.dynamicLayoutSettings;
        } else {
          this.buildEmptyLayout().subscribe((dynamicLayoutSettings) => {
            dynamicLayoutSettings.disableReorder = false;
            this.dynamicLayoutSettings = dynamicLayoutSettings;
            this.updateDashboard(dynamicLayoutSettings);
          });
        }
      });

    this._dashboardsService
      .getDashboardsGroupPermissions()
      .subscribe((dashboardGroupPermissions) => {
        this.dashboardGroupPermissions = dashboardGroupPermissions;
      });
  }

  private buildEmptyLayout(): Observable<DynamicLayoutSettings> {
    const simpleSettings: SimpleDynamicLayoutSettings = {
      // Each page has a differently persisted RP layout (the pageKey changes)
      layoutArea: this._persistencyArea,
      layoutKey: this._dashboard.title,
      items: [],
    };

    return this._dynamicLayoutBuilderService.build(simpleSettings).pipe(
      map((settings: DynamicLayoutSettings) => {
        // Although all pages have different persistency keys, they all have the same redux path.
        settings.widgetPage = this._statePage;
        settings.showCloseWidgetIcon = true;
        return settings;
      }),
    );
  }

  onAddWidget(): void {
    this._dashboardsService.createDashboardChildWidget().subscribe((widget) => {
      if (widget) {
        this._dynamicLayoutComponent.addWidgetAndSelect(widget);
      }
    });
  }

  onLayoutSettingsChanged(currentLayoutSettings: DynamicLayoutSettings): void {
    const previousLayoutSettings = this._currentLayoutSettings;
    this._currentLayoutSettings = currentLayoutSettings;

    const addedItem = this.findAddedItem(
      previousLayoutSettings?.items,
      currentLayoutSettings?.items,
    );
    if (addedItem) {
      return;
    }

    const changedItem = this.findChangedItem(
      previousLayoutSettings?.items,
      currentLayoutSettings?.items,
    );
    if (changedItem) {
      this._dynamicLayoutComponent.selectWidget(changedItem.widgetInstanceKey);
    }
  }

  onSaveDashboard(): void {
    this.updateDashboard(this._currentLayoutSettings);
  }

  private updateDashboard(dynamicLayoutSettings: DynamicLayoutSettings): void {
    const dashboardDefinition = {
      ...this._dashboard,
      dynamicLayoutSettings: dynamicLayoutSettings,
    };

    this.setLoading(true);
    this._dashboardsService
      .updateDashboard(dashboardDefinition)
      .pipe(finalize(() => this.setLoading(false)))
      .subscribe({
        next: () => {
          this._dialogService.showEntityActionSnackBar('update', 'dashboard.singular');
        },
        error: this._dialogService.showErrorMessage,
      });
  }

  private findAddedItem(
    previousItems: DynamicLayoutItemSettings[],
    currentItems: DynamicLayoutItemSettings[],
  ): DynamicLayoutItemSettings | undefined {
    if (!previousItems || !currentItems) return undefined;
    return currentItems.find(
      (newItem) =>
        !previousItems.some((oldItem) => oldItem.widgetInstanceKey === newItem.widgetInstanceKey),
    );
  }

  resetInnerLayout(): void {
    this._dynamicLayoutComponent?.resetToDefault();
  }

  private findChangedItem(
    previousItems: DynamicLayoutItemSettings[],
    currentItems: DynamicLayoutItemSettings[],
  ): DynamicLayoutItemSettings | undefined {
    if (!previousItems || !currentItems) return undefined;
    return currentItems.find((newItem) => {
      const previousItem = previousItems.find(
        (oldItem) => oldItem.widgetInstanceKey === newItem.widgetInstanceKey,
      );
      return previousItem && JSON.stringify(previousItem) !== JSON.stringify(newItem);
    });
  }

  get componentName(): string {
    return 'DashboardWidgetComponent';
  }
}
